<template>
  <div
    class="flex w-full flex-col"
    :class="[
      sortableActions.length > 0 && 'mt-4',
      sortableActions.length === 0 && isEditable && 'mt-2',
    ]"
  >
    <div v-if="actions.length > 0 && isEditable" class="flex w-full flex-col">
      <h4 class="text-title-small">{{ $t('objective.actions.title') }}</h4>
      <div class="flex flex-row items-center gap-2">
        <BaseLinearProgressIndictor
          :percentage="(actions.filter((action) => action.isChecked).length / actions.length) * 100"
        />
        <span class="text-label-medium text-on-surface-variant shrink-0 whitespace-nowrap">
          <span class="inline-block w-8 text-right">
            {{ percentageDoneString }}
          </span>
          {{ $t('objective.actionsCompleted') }}
        </span>
      </div>
    </div>
    <button
      v-if="!isEditable && actions.length > 0"
      @click="isExpanded = !isExpanded"
      class="flex flex-row items-center gap-2"
    >
      <span class="text-label-medium text-on-surface-variant shrink-0 whitespace-nowrap"
        >{{ percentageDoneString }} {{ $t('objective.actions.title') }}
        {{ $t('objective.actionsCompleted') }}</span
      >
      <BaseIcon class="text-on-surface-variant" :class="[isExpanded && 'rotate-180']" size="20"
        >keyboard_arrow_down</BaseIcon
      >
    </button>
    <BaseAnimatedExtend :is-extended="isExpanded">
      <ul
        ref="actionsList"
        class="flex shrink-0 grow-0 list-none! flex-col gap-0.5"
        :class="sortableActions.length > 0 && 'py-2'"
      >
        <li v-for="action in sortableActions" :key="action.id" class="list-none">
          <ObjectiveActionsListItem
            :action="action"
            :is-editable="isEditable"
            :autofocus="action.id === focusActionId"
            :number-of-actions="sortableActions.length"
            @set-checked="emits('setChecked', action, $event)"
            @rename="emits('rename', action, $event)"
            @delete="emits('delete', action)"
            @dirty-changed="onActionDirtyChanged(action.id, $event)"
          />
        </li>
      </ul>
    </BaseAnimatedExtend>
    <BaseTextButton
      v-if="isEditable"
      @click="emits('add')"
      icon="add"
      color="text"
      shape="square"
      size="XS"
      class="text-on-surface-variant! bg-surface-container-low w-full"
      >{{ $t('objective.actions.action') }}</BaseTextButton
    >
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
  import { moveArrayElement, useSortable } from '@vueuse/integrations/useSortable'
  import type { ObjectiveAction } from '@/core/composables/use-objectives'
  import ObjectiveActionsListItem from './ObjectiveActionsListItem.vue'
  import BaseTextButton from '../base/button/text-button/BaseTextButton.vue'
  import BaseLinearProgressIndictor from '../base/linear-progress-indicator/BaseLinearProgressIndictor.vue'
  import BaseAnimatedExtend from '../base/animated-extend/BaseAnimatedExtend.vue'
  import BaseIcon from '../base/icon/BaseIcon.vue'

  interface Props {
    actions: ReadonlyArray<ObjectiveAction>
    isEditable: boolean
    isAchieved: boolean
    focusActionId?: string
  }

  const props = defineProps<Props>()

  const emits = defineEmits<{
    (e: 'add'): void
    (e: 'setChecked', action: ObjectiveAction, isChecked: boolean): void
    (e: 'rename', action: ObjectiveAction, title: string): void
    (e: 'delete', action: ObjectiveAction): void
    (e: 'reorder', actionIds: string[]): void
    (e: 'dirty-changed', dirty: boolean): void
  }>()

  const dirtyActionIds = ref(new Set<string>())

  const onActionDirtyChanged = (actionId: string, dirty: boolean) => {
    if (dirty) {
      dirtyActionIds.value.add(actionId)
    } else {
      dirtyActionIds.value.delete(actionId)
    }
    emits('dirty-changed', dirtyActionIds.value.size > 0)
  }

  const actionsList = useTemplateRef<HTMLElement>('actionsList')
  const sortableActions = ref<ObjectiveAction[]>([])
  const isExpanded = ref(props.isAchieved)

  const percentageDoneString = computed(
    () => `${props.actions.filter((action) => action.isChecked).length}/${props.actions.length}`,
  )

  watch(
    () => props.actions,
    (actions) => {
      sortableActions.value = [...actions].sort((a, b) => a.position - b.position)
      const actionIds = new Set(actions.map((action) => action.id))
      dirtyActionIds.value = new Set([...dirtyActionIds.value].filter((id) => actionIds.has(id)))
      emits('dirty-changed', dirtyActionIds.value.size > 0)
    },
    { immediate: true },
  )

  useSortable(actionsList, sortableActions, {
    handle: '.action-drag-handle',
    animation: 150,

    ghostClass: 'action-sortable-ghost',
    chosenClass: 'action-sortable-chosen',
    dragClass: 'action-sortable-drag',
    fallbackClass: 'action-sortable-drag',
    fallbackOpacity: 1,
    forceFallback: true,
    fallbackOnBody: true,
    onUpdate: (event: {
      oldIndex?: number | null
      newIndex?: number | null
      item: HTMLElement
      from: HTMLElement
    }) => {
      if (event.oldIndex == null || event.newIndex == null) {
        return
      }
      moveArrayElement(sortableActions, event.oldIndex, event.newIndex, event)
      void nextTick(() => {
        emits(
          'reorder',
          sortableActions.value.map((action) => action.id),
        )
      })
    },
  })

  watch(
    () => props.isAchieved,
    (achieved) => {
      isExpanded.value = !achieved
    },
    { immediate: true },
  )
</script>

<style>
  .action-sortable-drag {
    z-index: 50;
    opacity: 1 !important;
    cursor: grabbing;
    background-color: var(--color-surface-container-high);
    border-radius: var(--radius-medium);
    box-shadow: var(--shadow-4);
    background: var(--color-secondary-container) !important;
  }
  .action-sortable-ghost {
    position: relative;
  }
  .action-sortable-ghost:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--color-surface-container) !important;
    border-radius: var(--radius-medium);
  }
</style>
